import React from "react";
import Modal from "react-bootstrap/Modal";
import Button, { ButtonVariant } from "./Button";

interface ConfirmDialogProps {
    show: boolean;
    /** What's being deleted (e.g. "Leg", "Itinerary", "Team"). Used in the title. */
    entity: string;
    /** Specific name of the row (e.g. "Leg 1", "Day 1 — Riyadh"). Shown in bold. */
    name?: string | null;
    /** Optional override for the body text. Defaults to a sensible "Are you sure" line.
     *  Accepts a ReactNode so callers can render bold names, line-breaks,
     *  or muted secondary copy without escaping HTML strings. */
    message?: React.ReactNode;
    /** Override for the title verb (default: "Delete"). Use this when the
     *  dialog confirms a non-deletion action like "Convert" or "Reopen". */
    titleVerb?: string;
    /** Override the trash icon shown in the title. Pass a bootstrap-icon
     *  class string (e.g. "bi-arrow-repeat") for non-destructive actions. */
    titleIcon?: string;
    /** Icon on the confirm button. Pass a bootstrap-icon class string
     *  (e.g. "bi-arrow-repeat"). */
    confirmIcon?: string;
    /** Confirm-button variant. Defaults to "danger-solid"; non-destructive
     *  actions (Convert, Reopen, Approve) pass "primary". */
    confirmVariant?: ButtonVariant;
    confirmLabel?: string;
    cancelLabel?: string;
    /** Test-id namespace for the footer buttons (`<scope>-accept` / `<scope>-cancel`).
     *  The global ConfirmProvider overrides this so e2e can auto-accept ONLY the
     *  confirms that replaced native `window.confirm`, without touching dialogs
     *  that specs drive explicitly (convert-to-mission, no-show, …). */
    testIdScope?: string;
    onConfirm: () => void;
    onCancel: () => void;
}

/**
 * Centered destructive-action confirm modal — themed for both light and
 * dark via the project's CSS variables. Used for deleting itineraries,
 * legs, teams, members, etc., so every removal surfaces a "are you sure
 * you want to delete X?" prompt with the row's name in bold.
 */
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    show,
    entity,
    name,
    message,
    titleVerb = "Delete",
    titleIcon = "bi-exclamation-triangle",
    confirmIcon,
    confirmVariant = "danger-solid",
    confirmLabel = "Delete",
    cancelLabel = "Cancel",
    testIdScope = "confirm-dialog",
    onConfirm,
    onCancel,
}) => {
    // Focus starts on the safe choice: Cancel when confirming something destructive.
    const isDestructive = confirmVariant === "danger" || confirmVariant === "danger-solid";
    return (
        <Modal show={show} onHide={onCancel} centered dialogClassName="crx-dialog-sm">
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className={`bi ${titleIcon} me-2`} />
                    {titleVerb} {entity}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ fontSize: "var(--fs-md)", color: "var(--color)" }}>
                {message ?? (
                    <>
                        Are you sure you want to delete{" "}
                        <strong>{name ? `"${name}"` : `this ${entity.toLowerCase()}`}</strong>?
                        <div className="text-muted small mt-2">
                            This action can't be undone from this screen.
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button type="button" variant="ghost" data-testid={`${testIdScope}-cancel`} onClick={onCancel} autoFocus={isDestructive}>
                    {cancelLabel}
                </Button>
                <Button
                    type="button"
                    variant={confirmVariant}
                    autoFocus={!isDestructive}
                    icon={confirmIcon?.replace(/^bi-/, "")}
                    data-testid={`${testIdScope}-accept`}
                    onClick={onConfirm}
                >
                    {confirmLabel}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ConfirmDialog;
